/**
 * NY ARBETSORDER — den enda skärmen i appen som SKAPAR stamdata.
 *
 * PROTOTYPEN HAR BARA KNAPPEN ("Ny arbetsorder" på Laget), aldrig fälten. Den
 * här skärmen är alltså ritad härifrån, mot kontraktet: WorkOrderWrite bär
 * customer_id, address, description, status och responsible_account_id, och
 * ingenting mer får skickas.
 *
 * KUNDEN SKAPAS I SAMMA FLÖDE, och det är hela poängen. En hantverkare som står
 * hos en ny kund ska inte behöva en dator först. Båda raderna går i ETT
 * SYNC_PUSH-kuvert, i ordningen kund -> arbetsorder: varje operation körs i sin
 * EGEN transaktion i kuvertets ordning, så kunden finns när arbetsordern slår upp
 * den (annars UNKNOWN_REFERENCE { table: 'customer' }).
 *
 * OP_ID MYNTAS EN GÅNG OCH ÄNDRAS ALDRIG (beslut 09). När Spara trycks fryses en
 * PLAN — id:n, op_id:n och kroppar — och fälten låsts. En omsändning skickar
 * SAMMA op_id med SAMMA kropp och får därför 'duplicate' i stället för en andra
 * kund. Skulle bara den ena operationen ha gått igenom skickas bara den andra om.
 * Vill användaren rätta något släpper han planen med "Ändra uppgifterna" — och då
 * sägs det också rakt ut att en redan skapad kund ligger kvar.
 *
 * BARA CHEFEN. 'put:work_order' och 'put:customer' är MANAGER_ONLY i SYNC_RULES,
 * och bokföraren står inte i EN ENDA rad där (beslut 07) trots att han ser
 * Laget. Skärmen ligger därför i läge 'lag' — en montör kan inte ens nå adressen
 * — och knappen finns bara för `arChef`.
 *
 * ROT-SATSEN SÄTTS HÄR, PÅ KUNDEN, och det är hela ROT-kedjan.
 * schema/company.sql rad 525-526: time_entry.rot_hundredths "fryses fran
 * customer.rot_hundredths och ar 0 om kunden saknar ROT". Momentets rot_eligible
 * säger VAD som ger ROT; kundens sats säger MED HUR MYCKET. Skrevs kunden med
 * rot_hundredths = null blev varje tidrad fryst med 0, ROT-kolumnen i CSV:n
 * skrev alltid '0', och ROT-krysset på momentskärmen var utan verkan — hur många
 * moment som än var märkta. Fältet gäller BARA privatkund: CHECK
 * (rot_hundredths IS NULL OR (kind = 'private' AND ...)) i schemat.
 * Satsen går att ändra i efterhand på skärmen 'kunder' (Kunder.tsx).
 */

import { useState } from 'react';
import type { CustomerId, CustomerKind, OpId, SyncOp, WorkOrderId } from '@delat/types';
import {
  FAKTOR,
  Falt,
  Felruta,
  Knapp,
  Laddar,
  Talfalt,
  Textyta,
  Tomtext,
  Valfalt,
  Yta,
  tillHeltal,
} from '../ui';
import { granska } from '../api';
import type { Klartidfel } from '../api';
import { MAX_ROT_HUNDRADELAR } from '../api/konstanter';
import { useInloggad } from '../app/session';
import { glomAllaFirmaminnen } from '../app/firmaminnen';
import { useNavigering } from '../app/navigering';
import {
  arKlar,
  forstaMisslyckade,
  nu,
  nyKundOp,
  nyttKundId,
  nyttOpId,
  nyttOrderId,
  skickaOps,
  useArbetsorderlager,
} from './arbetsorder-data';

const NY_KUND = 'NY';

interface Plan {
  /** null = kunden fanns redan. */
  readonly kundOp: SyncOp | null;
  /** Kunden arbetsordern pekar på — befintlig eller den som just skapas. */
  readonly kundId: CustomerId;
  readonly orderOp: SyncOp;
  readonly orderId: WorkOrderId;
}

export const NyOrder = () => {
  const { firmaId, arChef, arLast } = useInloggad();
  const { ga } = useNavigering();
  const { lage, laddaOm } = useArbetsorderlager(firmaId);

  const [kundval, sattKundval] = useState<string>(NY_KUND);
  const [kundnamn, sattKundnamn] = useState('');
  const [kundslag, sattKundslag] = useState<CustomerKind>('private');
  const [orgnr, sattOrgnr] = useState('');
  const [kundadress, sattKundadress] = useState('');
  /** Kundens ROT-sats i procent, som chefen skriver den. Tomt = ingen ROT. */
  const [rot, sattRot] = useState('');
  const [adress, sattAdress] = useState('');
  const [beskrivning, sattBeskrivning] = useState('');
  const [ansvarig, sattAnsvarig] = useState('');

  const [plan, sattPlan] = useState<Plan | null>(null);
  const [klara, sattKlara] = useState<readonly OpId[]>([]);
  const [sparar, sattSparar] = useState(false);
  const [fel, sattFel] = useState<Klartidfel | null>(null);
  const [faltfel, sattFaltfel] = useState<Readonly<Record<string, string>>>({});

  if (!arChef) {
    return (
      <>
        <Yta rubrik="Bara chefen lägger upp arbetsordrar">
          <p style={{ margin: 0 }}>
            Du ser hela laget, men arbetsordrar, kunder och prislistor skrivs av
            chefen. Det är samma gräns som i attesten.
          </p>
        </Yta>
        <Knapp variant="stor-tyst" onClick={() => ga('ordrar')}>
          Till arbetsordrarna
        </Knapp>
      </>
    );
  }

  if (lage.status === 'laddar') return <Laddar rader={5} />;
  if (lage.status === 'fel') {
    return (
      <Felruta
        fel={lage.fel}
        atgard={
          <Knapp variant="piller" onClick={laddaOm}>
            Försök igen
          </Knapp>
        }
      />
    );
  }

  const lager = lage.lager;
  const kunder = lager.kunder.slice().sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  const nyKund = kundval === NY_KUND || kunder.length === 0;
  const last = plan !== null;

  const kundvalslista = [
    { varde: NY_KUND, etikett: '+ Ny kund' },
    ...kunder.map((k) => ({ varde: k.id as string, etikett: k.name })),
  ];

  const ansvarslista = [
    { varde: '', etikett: 'Ingen ansvarig' },
    // Bokföraren kan inte äga en rad (triggrar i schema/company.sql) och ska
    // därför inte heller kunna stå som ansvarig för ett jobb.
    ...lager.medlemmar
      .filter((m) => m.role !== 'bookkeeper')
      .map((m) => ({ varde: m.account_id as string, etikett: m.name })),
  ];

  /** Klientens egen förkontroll. Servern gör den riktiga mot schemats CHECK. */
  const byggPlan = (): Plan | null => {
    const brister: Record<string, string> = {};
    const rensadBeskrivning = beskrivning.trim();
    if (rensadBeskrivning === '') brister['beskrivning'] = 'Skriv vad som ska göras.';
    if (rensadBeskrivning.length > 2000) brister['beskrivning'] = 'Högst 2 000 tecken.';
    if (adress.trim().length > 200) brister['adress'] = 'Högst 200 tecken.';

    let kundId: CustomerId | null = null;
    let kundOp: SyncOp | null = null;

    if (nyKund) {
      const namn = kundnamn.trim();
      if (namn === '') brister['kundnamn'] = 'Kunden behöver ett namn.';
      if (namn.length > 120) brister['kundnamn'] = 'Högst 120 tecken.';
      if (kundadress.trim().length > 200) brister['kundadress'] = 'Högst 200 tecken.';
      const siffror = orgnr.replace(/[\s-]/g, '');
      if (kundslag === 'company' && siffror !== '' && !/^\d{10}$/.test(siffror)) {
        brister['orgnr'] = 'Org.nr är tio siffror.';
      }
      // ROT-SATSEN. Tomt fält = ingen ROT (null), och den finns bara på
      // privatkund — samma gräns som schemats CHECK.
      const rotHundradelar =
        kundslag !== 'private' || rot.trim() === ''
          ? null
          : tillHeltal(rot.trim(), FAKTOR.procent_hundradel);
      if (kundslag === 'private' && rot.trim() !== '') {
        if (rotHundradelar === null) brister['rot'] = 'Skriv satsen i procent, till exempel 30.';
        else if (rotHundradelar < 0 || rotHundradelar > MAX_ROT_HUNDRADELAR) {
          brister['rot'] = 'ROT-satsen ligger mellan 0 och 100 procent.';
        }
      }
      if (Object.keys(brister).length === 0) {
        kundId = nyttKundId();
        kundOp = nyKundOp(
          kundId,
          {
            name: namn,
            kind: kundslag,
            // CHECK (kind = 'company' OR org_number IS NULL): en privatperson
            // lagras ALDRIG med personnummer.
            org_number:
              kundslag === 'company' && siffror !== ''
                ? granska.orgnummer(siffror, 'org_number')
                : null,
            address: kundadress.trim() === '' ? null : kundadress.trim(),
            // Prisundantaget (beslut 15) sätts inte här; null = firmans
            // standardlista. Se LUCKOR.
            price_list_id: null,
            rot_hundredths:
              rotHundradelar === null
                ? null
                : granska.hundradelar(rotHundradelar, 'rot_hundredths'),
          },
          nyttOpId(),
        );
      }
    } else {
      kundId = kunder.find((k) => k.id === kundval)?.id ?? null;
      if (kundId === null) brister['kund'] = 'Välj en kund.';
    }

    if (Object.keys(brister).length > 0 || kundId === null) {
      sattFaltfel(brister);
      return null;
    }
    sattFaltfel({});

    const orderId = nyttOrderId();
    const orderOp: SyncOp = {
      op_id: nyttOpId(),
      client_time: nu(),
      kind: 'put',
      base_version: null,
      table: 'work_order',
      id: orderId,
      row: {
        customer_id: kundId,
        address: adress.trim() === '' ? null : adress.trim(),
        description: rensadBeskrivning,
        status: 'open',
        responsible_account_id:
          ansvarig === '' ? null : granska.accountId(ansvarig, 'responsible_account_id'),
        // Arbetsorderns prisundantag sätts inte här; null = kundens eller
        // firmans lista avgör (beslut 15, upplösningsordningen).
        price_list_id: null,
      },
    };
    return { kundOp, kundId, orderOp, orderId };
  };

  const spara = (): void => {
    const aktuell = plan ?? byggPlan();
    if (aktuell === null) return;
    if (plan === null) sattPlan(aktuell);

    const attSkicka: SyncOp[] = [];
    if (aktuell.kundOp !== null && !klara.includes(aktuell.kundOp.op_id)) {
      attSkicka.push(aktuell.kundOp);
    }
    if (!klara.includes(aktuell.orderOp.op_id)) attSkicka.push(aktuell.orderOp);
    if (attSkicka.length === 0) {
      // ALLA LAGREN: en ny arbetsorder ska finnas i tidrapporten pa en gang,
      // och den listan halls i en annan modul. Se app/firmaminne.ts.
      glomAllaFirmaminnen();
      ga('order', { work_order_id: aktuell.orderId });
      return;
    }

    sattSparar(true);
    sattFel(null);
    void (async () => {
      const svar = await skickaOps(firmaId, attSkicka);
      sattSparar(false);
      if (!svar.ok) {
        sattFel(svar.fel);
        return;
      }
      const nyaKlara = [...klara];
      for (const op of attSkicka) if (arKlar(svar.varde, op.op_id)) nyaKlara.push(op.op_id);
      sattKlara(nyaKlara);
      const opsfel = forstaMisslyckade(svar.varde);
      if (opsfel !== null) {
        sattFel(opsfel);
        return;
      }
      // Lyckad push -> lagren är inaktuella; detaljen och tidrapporten hämtar om.
      glomAllaFirmaminnen();
      ga('order', { work_order_id: aktuell.orderId });
    })();
  };

  return (
    <>
      <Yta rubrik="Kund">
        <Valfalt
          etikett="Vilken kund?"
          varde={nyKund ? NY_KUND : kundval}
          onAndra={sattKundval}
          val={kundvalslista}
          disabled={last}
          {...(faltfel['kund'] === undefined ? {} : { fel: faltfel['kund'] })}
          {...(kunder.length === 0
            ? { hjalp: 'Firman har inga kunder än — den första läggs upp här.' }
            : {})}
        />
        {nyKund && (
          <>
            <Falt
              etikett="Kundens namn"
              varde={kundnamn}
              onAndra={sattKundnamn}
              disabled={last}
              placeholder="Anna Andersson eller Brf Tallmon"
              {...(faltfel['kundnamn'] === undefined ? {} : { fel: faltfel['kundnamn'] })}
            />
            <Valfalt
              etikett="Privat eller företag"
              varde={kundslag}
              onAndra={sattKundslag}
              disabled={last}
              val={[
                { varde: 'private', etikett: 'Privatkund' },
                { varde: 'company', etikett: 'Företagskund' },
              ]}
              hjalp="Privatkund lagras aldrig med personnummer."
            />
            {kundslag === 'company' && (
              <Falt
                etikett="Org.nr"
                varde={orgnr}
                onAndra={sattOrgnr}
                disabled={last}
                inputMode="numeric"
                placeholder="5565551234"
                hjalp="Tio siffror. Kan lämnas tomt."
                {...(faltfel['orgnr'] === undefined ? {} : { fel: faltfel['orgnr'] })}
              />
            )}
            <Falt
              etikett="Kundens adress"
              varde={kundadress}
              onAndra={sattKundadress}
              disabled={last}
              placeholder="Tallmovägen 22, 122 40 Enskede"
              {...(faltfel['kundadress'] === undefined ? {} : { fel: faltfel['kundadress'] })}
            />
            {/* ROT BARA PÅ PRIVATKUND — schemats CHECK säger samma sak. Fältet
                är det ENDA stället satsen kommer in på en ny kund, och utan den
                fryses varje tidrad med 0 hur många moment som än är ROT-märkta. */}
            {kundslag === 'private' && (
              <Talfalt
                etikett="ROT-sats"
                varde={rot}
                onAndra={(text) => sattRot(text)}
                faktor={FAKTOR.procent_hundradel}
                suffix="%"
                disabled={last}
                placeholder="30"
                hjalp="Lämna tomt om jobbet inte ger ROT. Klartid räknar aldrig ut avdraget — satsen följer med underlaget ut."
                {...(faltfel['rot'] === undefined ? {} : { fel: faltfel['rot'] })}
              />
            )}
          </>
        )}
      </Yta>

      {/* KUNDEN FINNS REDAN: satsen ändras på kundskärmen, inte här. En
          arbetsorder ska inte kunna skriva om en kund i förbifarten. */}
      {!nyKund && (
        <Tomtext>
          ROT-satsen sitter på kunden och ändras under Inställningar → Kunder.
          Saknas den fryses varje timme på jobbet utan ROT.
        </Tomtext>
      )}

      <Yta rubrik="Jobbet">
        <Falt
          etikett="Adress på jobbet"
          varde={adress}
          onAndra={sattAdress}
          disabled={last}
          placeholder="Tallmovägen 22, lgh 1102"
          hjalp="Lämna tomt så gäller kundens adress."
          {...(faltfel['adress'] === undefined ? {} : { fel: faltfel['adress'] })}
        />
        <Textyta
          etikett="Vad ska göras?"
          varde={beskrivning}
          onAndra={sattBeskrivning}
          disabled={last}
          rows={4}
          placeholder="Byte av vattenutkastare. Stäng av stammen, provtryck efteråt."
          hjalp="En rad per punkt — texten blir både rubrik och att göra-lista på jobbet."
          {...(faltfel['beskrivning'] === undefined ? {} : { fel: faltfel['beskrivning'] })}
        />
        <Valfalt
          etikett="Vem ansvarar?"
          varde={ansvarig}
          onAndra={sattAnsvarig}
          val={ansvarslista}
          disabled={last}
          hjalp="Bara sortering — alla får rapportera på jobbet ändå."
        />
      </Yta>

      {fel !== null && <Felruta fel={fel} />}

      <Knapp
        disabled={arLast || sparar}
        onClick={spara}
        title={arLast ? 'Prenumerationen är pausad. Inget nytt kan sparas.' : undefined}
      >
        {sparar ? 'Sparar...' : plan === null ? 'Spara arbetsordern' : 'Försök igen'}
      </Knapp>

      {plan !== null && !sparar && (
        <div style={{ marginTop: '0.5rem' }}>
          <Knapp
            variant="stor-tyst"
            onClick={() => {
              // KUNDEN SKAPAS ALDRIG TVÅ GÅNGER. Gick kundoperationen igenom
              // men arbetsordern föll, så finns kunden redan i firman — då
              // pekas formuläret om PÅ HENNE i stället för att nästa försök ska
              // lägga upp en dubblett med ett nytt id.
              if (plan.kundOp !== null && klara.includes(plan.kundOp.op_id)) {
                sattKundval(plan.kundId);
                laddaOm();
              }
              sattPlan(null);
              sattKlara([]);
              sattFel(null);
            }}
          >
            Ändra uppgifterna
          </Knapp>
        </div>
      )}

      {plan !== null && klara.length > 0 && (
        <Tomtext>
          En del av inlämningen gick igenom. Kunden är redan upplagd — ändrar du
          uppgifterna nu väljs hon i listan i stället för att läggas upp igen.
        </Tomtext>
      )}

      {plan === null && (
        <Tomtext>
          Arbetsordern går till alla direkt. Priset sitter på rollen och fryses först
          när någon rapporterar tid — inget timpris skrivs här.
        </Tomtext>
      )}
    </>
  );
};
